// WHY a tool call failed — the closed vocabulary stored beside the prose on a
// `tool.call.err` row (`output.failureReason`).
//
// A refusal used to be one thing: `output.error`, a sentence. The sentences were
// ranked by tallying that sentence (`->>'error'` substring in SQL), which is why
// the plan census could not answer "which tool fails, and why" without a human
// reading 47KB rows: a 404, a hand-written bot footer and a tool whose results
// stopped matching its declared output all read as one unranked number. The
// machine fields beside it (`failureCode`, `failureStatus`) come from the raiser
// too, and they are optional — only `failureReason` groups a row.
//
// ONE vocabulary, here, because three packages need it and none may import
// another: `@developerz/agent` writes it (`runToolWithAudit`,
// `loop/audit.ts`), `@developerz/tools` raises the refusals that carry it, and
// `@developerz/db` groups on it in SQL (`queries/tool-call-tally.ts`,
// `queries/admin/health.ts`). Each grows a member or the three drift, and a
// stored row outlives the build that wrote it: a reader folds any value outside
// this set to `unrecognized` rather than printing a drifted writer's invention,
// so a new member is safe to ADD and impossible to rename in place.
//
// THE CLASSIFIER IS STRUCTURAL, never `instanceof`. This package imports nothing
// internal, and `packages/github` may not import domain — so neither
// `GitHubRestError` nor `ToolOutputContractError` can be named from here, and a
// raised class cannot be matched to a cause by its identity. {@link
// toolFailureOf} therefore reads the fields the raiser already carries.

import { z } from 'zod';

/**
 * The closed set. Each member is one CAUSE a reader can act on, not a message and
 * not a tool:
 *
 * - `read_file_*` — the six refusals of `gh.repo.read_file`
 *   (`packages/tools/src/github/repo.ts`), one per branch: a path GitHub does not
 *   have, a directory (historical: old audit rows carry it; nothing produces it since
 *   2026-11-01, when a directory read became a `kind: 'dir'` listing), a non-file entry, a file over GitHub's 1 MB `/contents`
 *   limit, an answer with no `content`, and an `encoding` that is not base64. They
 *   are the highest-volume refusals a session makes (939 of 2,962 `read_file`
 *   calls 404'd in the 7 days to 2026-10-11), and each names a different fix, so
 *   one `read_file_refused` bucket would be the defect the key exists to remove.
 * - `manual_bot_markup` — `assertNoManualBotMarkup` (`packages/tools/src/github/util.ts`):
 *   a model writing the bot's own footer or a forged bot voice (#645). The tool
 *   appends the real one, so this is a double voice, not a formatting nit.
 * - `fence_echo` — `assertNotFenceEcho` (`packages/tools/src/github/untrusted.ts`): a
 *   body bound for GitHub is a read's fence coming back, not the writer's own words.
 * - `expectation_required` — a consequential call lacks an accepted expectation.
 * - `sdk_schema` — the SDK rejected tool input against its schema.
 * - `in_flight_lost` — a reserved side effect has no confirmed outcome.
 * - `github_throttle` — the REST seam stamped `throttled`, or answered HTTP 429.
 * - `github_rest` — another REST refusal: a `GitHubRestError` or HTTP status.
 *   Read structurally (below), since this package cannot name the class.
 * - `tool_output_contract` — a tool's own result failed its declared `output`
 *   schema (`parseToolOutput`, `packages/tools/src/contract.ts`). A platform
 *   defect in the tool, not something a different argument fixes.
 * - `unknown` — THE RESIDUAL: nothing on the thrown value names a cause. It is a
 *   classifier answer, NEVER a stored value: the writer persists `failureReason`
 *   only when the reason is not the residual, so an unclassified refusal stays the
 *   plain `{ error }` row it has always been and the tallies never grow a bucket
 *   for what nobody could name.
 */
export const ToolFailureReason = {
  ReadFileNotFound: 'read_file_not_found',
  ReadFileIsDirectory: 'read_file_is_directory',
  ReadFileNotAFile: 'read_file_not_a_file',
  ReadFileTooLarge: 'read_file_too_large',
  ReadFileNoContent: 'read_file_no_content',
  ReadFileUnexpectedEncoding: 'read_file_unexpected_encoding',
  ManualBotMarkup: 'manual_bot_markup',
  FenceEcho: 'fence_echo',
  ExpectationRequired: 'expectation_required',
  SdkSchema: 'sdk_schema',
  GithubThrottle: 'github_throttle',
  InFlightLost: 'in_flight_lost',
  GithubRest: 'github_rest',
  ToolOutputContract: 'tool_output_contract',
  Unknown: 'unknown',
} as const;

export const ToolFailureReasonSchema = z.enum(ToolFailureReason);

export type ToolFailureReason = z.infer<typeof ToolFailureReasonSchema>;

/**
 * Every member, for the readers that must agree on the SET rather than on a
 * spelling: the two tallies fold a stored value through it in SQL
 * (`packages/db`), and a test asserts a named cause is a member instead of
 * restating the word.
 */
export const TOOL_FAILURE_REASONS: readonly ToolFailureReason[] = ToolFailureReasonSchema.options;

/**
 * A refusal that names its own cause — how a tool says so. `extends Error`, so
 * every existing reader (the loop's retry decisions, the model's error result, the
 * tests asserting refusal text) is unchanged: this adds ONE field, and the
 * message stays the refusal's own.
 *
 * The cause travels on a FIELD, not in the message: the message is prose a model
 * reads and a human debugs, and a tally that groups on it is back to counting
 * sentences.
 */
export class ToolFailureError extends Error {
  readonly failureReason: ToolFailureReason;

  constructor(reason: ToolFailureReason, message: string) {
    super(message);
    this.name = 'ToolFailureError';
    this.failureReason = reason;
  }
}

/**
 * What {@link toolFailureOf} could name about a thrown value. `code` and `status`
 * are the raiser's OWN machine fields, carried beside the reason and omitted
 * entirely when the raiser holds none — never defaulted to a value it did not
 * have, because a reader storing `failureStatus: 0` would grade a status that
 * never happened.
 */
export type ToolFailure = {
  readonly reason: ToolFailureReason;
  /** Zod issue code of a contract failure (`invalid_type`, `too_big`, …). */
  readonly code?: string;
  /** HTTP status of a REST refusal (`404`, `422`, …). */
  readonly status?: number;
};

/** A thrown value's fields the classifier reads, each `null` when it carries none. */
type FailureShape = {
  readonly marker: string | null;
  readonly kind: string | null;
  readonly name: string | null;
  readonly code: string | null;
  readonly status: number | null;
  readonly ledgerStatus: string | null;
  readonly throttled: boolean;
};

const NO_SHAPE: FailureShape = {
  marker: null,
  kind: null,
  name: null,
  code: null,
  status: null,
  ledgerStatus: null,
  throttled: false,
};

/** `ToolOutputContractError.kind` — the marker it stamps on itself. */
const CONTRACT_KIND = 'tool_output_contract';

/**
 * The CAUSE of a thrown value, read STRUCTURALLY (see the header): its own
 * `failureReason` marker first, else the shape its raiser carries.
 *
 * The residual `unknown` is the honest answer for a bare `Error` and for anything
 * that is not an object at all. It is the writer's job not to persist it, because
 * a stored bucket must name a cause — this function cannot know whether its
 * caller will store the answer.
 *
 * Never throws: it runs on the audit path, where a classifier that raised would
 * take the `tool.call.err` row with it — the row the failure is recorded IN. A
 * value whose fields throw to read is therefore classified as the residual.
 */
export function toolFailureOf(err: unknown): ToolFailure {
  const shape = shapeOf(err);
  const reason =
    shape.marker !== null && isFailureReason(shape.marker) ? shape.marker : reasonOf(shape);
  const failure: { reason: ToolFailureReason; code?: string; status?: number } = { reason };
  if (shape.code !== null) failure.code = shape.code;
  if (shape.status !== null) failure.status = shape.status;
  return failure;
}

/**
 * The cause a value's SHAPE names, for a raiser that carries no marker of its own.
 *
 * The contract refusal is read FIRST: it names itself with a `kind` no other
 * raiser stamps, and that marker is more specific than the HTTP shape below, so a
 * value carrying both is graded as what it said it was.
 *
 * Named expectation, SDK and ledger refusals precede HTTP classification. The
 * ledger's string status is separate from the optional numeric HTTP metadata.
 * Throttling splits the generic REST bucket using the raiser's boolean or 429,
 * never by reinterpreting its message or body.
 *
 * A status names the REST seam on its own. The only HTTP a tool holds is
 * `ToolContext.github`, and `GitHubRestError` puts its status on the value, so
 * the number is enough — which matters precisely because the class is not
 * importable here.
 */
function reasonOf(shape: FailureShape): ToolFailureReason {
  if (shape.kind === CONTRACT_KIND || shape.name === 'ToolOutputContractError') {
    return ToolFailureReason.ToolOutputContract;
  }
  if (shape.name === 'ExpectationRequiredError') return ToolFailureReason.ExpectationRequired;
  if (shape.name === 'AI_InvalidToolInputError' || shape.name === 'AI_TypeValidationError') {
    return ToolFailureReason.SdkSchema;
  }
  if (shape.name === 'SideEffectInFlightLostError' || shape.ledgerStatus === 'in_flight_lost') {
    return ToolFailureReason.InFlightLost;
  }
  if (shape.status !== null || shape.name === 'GitHubRestError') {
    return shape.throttled || shape.status === 429
      ? ToolFailureReason.GithubThrottle
      : ToolFailureReason.GithubRest;
  }
  return ToolFailureReason.Unknown;
}

function shapeOf(err: unknown): FailureShape {
  if (err === null || typeof err !== 'object') return NO_SHAPE;
  try {
    const fields = err as Record<string, unknown>;
    const status = fields['status'];
    return {
      marker: text(fields['failureReason']),
      kind: text(fields['kind']),
      name: text(fields['name']),
      code: text(fields['code']),
      status: httpStatus(status),
      ledgerStatus: text(status),
      throttled: fields['throttled'] === true,
    };
  } catch {
    return NO_SHAPE;
  }
}

function text(raw: unknown): string | null {
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

/**
 * An HTTP status and nothing else: an integer in 100–599. The bound is the rule,
 * not decoration — an error that happens to carry some other number (`status: 0`
 * on a driver sentinel, a non-status counter) must not be graded as a REST
 * refusal it never was.
 */
function httpStatus(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 100 && raw <= 599 ? raw : null;
}

function isFailureReason(value: string): value is ToolFailureReason {
  return (TOOL_FAILURE_REASONS as readonly string[]).includes(value);
}
